/**
 * CurrentPicks — Active OPEN positions with live unrealized P&L.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, TrendingUp, TrendingDown, Target, RefreshCw, ArrowRightLeft, ArrowDownCircle, ArrowUpCircle, Sparkles, History, Clock, AlertTriangle, CheckCircle2, ShieldAlert, Info } from 'lucide-react';
import { formatPct, formatMoney, timeAgo } from './utils';
import { SortHeader, useSortable } from './SortableHeader';
import EventBadge from './EventBadge';
import NewsLegend from './NewsLegend';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CHURN_WINDOWS = [
  { key: '1d', label: 'Today', days: 1 },
  { key: '7d', label: '7 Days', days: 7 },
  { key: '30d', label: '30 Days', days: 30 },
  { key: 'all', label: 'All', days: null },
];

// Session 147a — hard cap on rendered active positions. The backend
// already enforces TOP_PICKS_COUNT=15 + dedupe-at-write + dedupe-at-read,
// but this is the final frontend safety belt that guarantees the user
// will never again see "17 instead of 15" even if a backend regression
// re-introduces the duplication bug class.
const MAX_VISIBLE_POSITIONS = 15;

const CurrentPicks = ({ profile = 'largecap', readOnly = false }) => {
  const { authFetch } = useAuth();
  const [positions, setPositions] = useState([]);
  const [closedRecent, setClosedRecent] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [churnWindow, setChurnWindow] = useState('7d');
  const [cronHeartbeat, setCronHeartbeat] = useState(null);
  // Session 2026-07-26 — daily bhavcopy pipeline status pill
  const [bhavStatus, setBhavStatus] = useState(null);
  // Session 147i — ML overlay: per-symbol p_up + verdict from SmartFlow
  // model. Single fetch covers both the active-positions table and the
  // Today's Rebalance chips.
  const [mlOverlay, setMlOverlay] = useState({ rows: [], trade_date: null });

  // Session 2026-07-26 — Smart-Exit retrospective replays keyed by
  // "SYMBOL::entry_date" so we can annotate every CLOSED row with what
  // the exit ladder would have done.
  const [replaysByKey, setReplaysByKey] = useState({});

  // Session 147j — profile-aware fetch
  const profileQ = `profile=${encodeURIComponent(profile)}`;

  const loadAll = useCallback(async () => {
    try {
      const [rPos, rSum, rClosed, rCron, rReplays, rBhav] = await Promise.all([
        authFetch(`${API}/api/alphascout/positions/open?${profileQ}`),
        authFetch(`${API}/api/alphascout/positions/summary?${profileQ}`),
        authFetch(`${API}/api/alphascout/positions/closed?limit=200&${profileQ}`),
        authFetch(`${API}/api/alphascout/cron-heartbeat`),
        authFetch(`${API}/api/alphascout/positions/replays?${profileQ}`),
        authFetch(`${API}/api/alphascout/bhavcopy-status`),
      ]);
      const dPos = await rPos.json();
      const dSum = await rSum.json();
      const dClosed = await rClosed.json();
      const dCron = await rCron.json().catch(() => null);
      const dReplays = await rReplays.json().catch(() => ({ replays: [] }));
      const dBhav = await rBhav.json().catch(() => null);
      setPositions(dPos.positions || []);
      setSummary(dSum);
      setClosedRecent(dClosed.positions || []);
      setCronHeartbeat(dCron);
      setBhavStatus(dBhav);

      // Index replays by SYMBOL::entry_date for O(1) lookup while rendering
      const rmap = {};
      (dReplays.replays || []).forEach((r) => {
        if (!r?.symbol || !r?.entry_date) return;
        rmap[`${r.symbol.toUpperCase()}::${r.entry_date.slice(0, 10)}`] = r;
      });
      setReplaysByKey(rmap);

      // Session 147i — fetch ML overlay for the union of open + recent
      // closed symbols so the Today's Rebalance card can score buys.
      try {
        const sym = new Set();
        (dPos.positions || []).forEach((p) => p.symbol && sym.add(p.symbol.toUpperCase()));
        (dClosed.positions || []).slice(0, 60).forEach((p) => p.symbol && sym.add(p.symbol.toUpperCase()));
        if (sym.size > 0) {
          // Session 147j+1 — URL-encode the comma-joined symbol list.
          // CRITICAL: symbols can contain `&` (e.g. "J&KBANK"), which would
          // otherwise be parsed by FastAPI as a separate query parameter
          // and silently truncate the request — collapsing the ML overlay
          // to a single symbol. encodeURIComponent keeps the comma
          // separators intact while escaping `&`, `+`, `#`, etc.
          const symbolsParam = encodeURIComponent([...sym].join(','));
          const r = await authFetch(`${API}/api/alphascout/ml-overlay?symbols=${symbolsParam}`);
          const d = await r.json();
          setMlOverlay(d || { rows: [] });
        }
      } catch (e) {
        // ML overlay is best-effort — never blocks the main dashboard.
        console.warn('[CurrentPicks] ML overlay fetch failed', e);
      }
    } catch (e) {
      console.error('[CurrentPicks] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [authFetch, profileQ]);

  const forceUpdate = async () => {
    setRefreshing(true);
    try {
      await authFetch(`${API}/api/alphascout/positions/update-eod?${profileQ}`, { method: 'POST' });
      await loadAll();
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  const sortAccessors = useMemo(() => ({
    entry_date: (r) => (r.entry_date ? new Date(r.entry_date).getTime() : 0),
  }), []);

  // Session 147a — dedupe positions by symbol at render time, keeping
  // the EARLIEST entry_date row (matches backend list_open semantics).
  // Hard cap to MAX_VISIBLE_POSITIONS so the user can never see >15.
  const { uniquePositions, dropped } = useMemo(() => {
    const bySym = new Map();
    for (const p of positions || []) {
      const sym = (p?.symbol || '').toUpperCase();
      if (!sym) continue;
      const existing = bySym.get(sym);
      if (!existing) {
        bySym.set(sym, p);
        continue;
      }
      const existingT = existing.entry_date ? new Date(existing.entry_date).getTime() : Infinity;
      const newT = p.entry_date ? new Date(p.entry_date).getTime() : Infinity;
      if (newT < existingT) bySym.set(sym, p);
    }
    const unique = Array.from(bySym.values());
    return {
      uniquePositions: unique,
      dropped: (positions || []).length - unique.length,
    };
  }, [positions]);

  const { sorted, field, dir, toggle } = useSortable(uniquePositions, {
    initialField: 'composite_score_at_entry',
    initialDir: 'desc',
    accessors: sortAccessors,
  });

  // Apply the hard 15-cap AFTER sorting so the highest-score 15 win.
  const cappedSorted = sorted.slice(0, MAX_VISIBLE_POSITIONS);
  const overCap = sorted.length - cappedSorted.length;

  // Session 147i — symbol→ML row lookup for fast badge rendering.
  const mlBySymbol = useMemo(() => {
    const m = new Map();
    (mlOverlay?.rows || []).forEach((r) => {
      if (r?.symbol) m.set(r.symbol.toUpperCase(), r);
    });
    return m;
  }, [mlOverlay]);

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!positions.length) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end gap-2">
          <BhavcopyStatusMarker bs={bhavStatus} />
          <CronHeartbeatMarker hb={cronHeartbeat} />
        </div>
        <div className="bg-surface border border-border p-10 text-center" data-testid="current-picks-empty">
          <Target className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-foreground font-semibold mb-1">No active positions</p>
          <p className="text-xs text-muted-foreground">Run a rebalance to populate your current picks.</p>
        </div>
      </div>
    );
  }

  const open = summary?.open || {};
  const closed = summary?.closed || {};

  // Date range context: use earliest entry from positions as the "since" anchor
  const earliestEntry = (() => {
    const dates = uniquePositions.map((p) => p.entry_date).filter(Boolean).sort();
    return dates[0] || open.earliest_entry_date || null;
  })();
  const sinceLabel = earliestEntry ? earliestEntry.slice(0, 10) : null;
  const lastUpdate = open.last_price_update ? open.last_price_update.slice(0, 10) : null;
  const todayLabel = new Date().toISOString().slice(0, 10);

  // Session 145h — today's rebalance delta. Stocks entered/exited today
  // that the user needs to action.
  const todaysExits = (closedRecent || []).filter(
    (p) => (p.exit_date || '').slice(0, 10) === todayLabel,
  );
  const todaysEntries = (uniquePositions || []).filter(
    (p) => (p.entry_date || '').slice(0, 10) === todayLabel,
  );
  const hasTodaysRebalance = todaysExits.length > 0 || todaysEntries.length > 0;

  // Session 146 — churn window filter. Picks closed within the selected
  // lookback surface as a dedicated "Recently Closed" section so the
  // user never loses sight of exits.
  const windowSpec = CHURN_WINDOWS.find((w) => w.key === churnWindow) || CHURN_WINDOWS[1];
  const nowMs = Date.now();
  const cutoffMs = windowSpec.days == null ? null : nowMs - windowSpec.days * 86400000;
  const closedWindow = (closedRecent || []).filter((p) => {
    if (cutoffMs == null) return true;
    const t = p.exit_date ? new Date(p.exit_date).getTime() : 0;
    return t >= cutoffMs;
  });
  const newInWindow = (uniquePositions || []).filter((p) => {
    if (cutoffMs == null) return true;
    const t = p.entry_date ? new Date(p.entry_date).getTime() : 0;
    return t >= cutoffMs;
  });
  const newInWindowSyms = new Set(newInWindow.map((p) => p.symbol));

  return (
    <div className="space-y-4" data-testid="current-picks">
      {/* Session 147a — Integrity guard banner. Surfaces when the
          backend leaks duplicates or over-cap rows so the user sees
          why the table differs from the raw response. */}
      {(dropped > 0 || overCap > 0) && (
        <div
          className="bg-amber-500/10 border border-amber-500/40 px-3 py-2 flex items-start gap-2 text-[11px] text-amber-200"
          data-testid="picks-integrity-warning"
        >
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold uppercase tracking-wider">
              Integrity guard:
            </span>{' '}
            {dropped > 0 && (
              <span>
                merged {dropped} duplicate {dropped === 1 ? 'row' : 'rows'} by symbol.{' '}
              </span>
            )}
            {overCap > 0 && (
              <span>
                clipped {overCap} {overCap === 1 ? 'row' : 'rows'} above the {MAX_VISIBLE_POSITIONS}-stock cap.
              </span>
            )}
            <span className="text-amber-300/70 ml-1">
              Showing {cappedSorted.length}/{MAX_VISIBLE_POSITIONS}.
            </span>
          </div>
        </div>
      )}

      {/* Today's Rebalance banner — stocks to exit and enter RIGHT NOW */}
      {hasTodaysRebalance && (
        <TodaysRebalanceBanner
          exits={todaysExits}
          entries={todaysEntries}
          todayLabel={todayLabel}
          mlBySymbol={mlBySymbol}
        />
      )}

      {/* Date range context strip (always shown — acts as container for legend) */}
      <div
        className="flex items-center flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground border-l-2 border-accent/60 pl-3 py-1"
        data-testid="current-picks-range"
      >
        {sinceLabel ? (
          <>
            <span>
              Returns since{' '}
              <span className="text-foreground font-mono font-semibold">{sinceLabel}</span>
              <span className="mx-1.5 text-muted-foreground/60">→</span>
              <span className="text-foreground font-mono font-semibold">{todayLabel}</span>
            </span>
            <span className="text-muted-foreground/60">·</span>
            <span>
              {cappedSorted.length} active {cappedSorted.length === 1 ? 'position' : 'positions'}
            </span>
            {lastUpdate && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span>
                  Prices last updated{' '}
                  <span className="text-foreground font-mono">{lastUpdate}</span>
                </span>
              </>
            )}
          </>
        ) : (
          <span>No active positions yet</span>
        )}
        <span className="ml-auto flex items-center gap-3">
          <BhavcopyStatusMarker bs={bhavStatus} />
          <CronHeartbeatMarker hb={cronHeartbeat} />
          <NewsLegend />
        </span>
      </div>

      {/* Portfolio summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryCard label="Active Positions" value={open.count} />
        <SummaryCard
          label="Avg Unrealized"
          value={formatPct(open.avg_unrealized_return_pct)}
          positive={open.avg_unrealized_return_pct > 0}
        />
        <SummaryCard
          label="Best Performer"
          value={
            open.best?.symbol && (open.best?.return_pct !== 0 || (open.worst?.return_pct || 0) !== 0)
              ? open.best.symbol
              : '–'
          }
          subvalue={
            open.best?.symbol && (open.best?.return_pct !== 0 || (open.worst?.return_pct || 0) !== 0)
              ? (open.best.status === 'CLOSED'
                  ? `${formatPct(open.best.return_pct)} · closed`
                  : formatPct(open.best.return_pct))
              : 'prices update EOD'
          }
          positive={(open.best?.return_pct || 0) > 0}
        />
        <SummaryCard
          label="Worst Performer"
          value={
            open.worst?.symbol && (open.best?.return_pct !== 0 || (open.worst?.return_pct || 0) !== 0)
              ? open.worst.symbol
              : '–'
          }
          subvalue={
            open.worst?.symbol && (open.best?.return_pct !== 0 || (open.worst?.return_pct || 0) !== 0)
              ? (open.worst.status === 'CLOSED'
                  ? `${formatPct(open.worst.return_pct)} · closed`
                  : formatPct(open.worst.return_pct))
              : 'prices update EOD'
          }
          positive={(open.worst?.return_pct || 0) > 0}
        />
        {/* Session 147j+16g — Max Drawdown (peak-to-trough since each
            position's entry_date, averaged across open book). */}
        <SummaryCard
          label="Max Drawdown"
          value={
            open.max_drawdown_pct != null ? formatPct(open.max_drawdown_pct) : '–'
          }
          subvalue={
            open.worst_drawdown?.symbol
              ? `worst ${open.worst_drawdown.symbol} ${formatPct(open.worst_drawdown.drawdown_pct)}`
              : 'since entry'
          }
          positive={false}
          testid="kpi-max-drawdown"
        />
        <div className="flex items-end justify-end">
          {!readOnly && (
          <button
            onClick={forceUpdate}
            disabled={refreshing}
            className="text-xs px-3 py-2 bg-surface-highlight border border-border text-muted-foreground hover:text-foreground flex items-center gap-1.5"
            data-testid="refresh-eod-btn"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh EOD
          </button>
          )}
        </div>
      </div>

      {/* Active positions table */}
      <div className="bg-surface border border-border overflow-hidden">
        <div className="px-3 py-2 border-b border-border bg-surface-highlight flex items-center gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Active Positions · {cappedSorted.length}
            <span className="ml-1.5 text-muted-foreground/60 normal-case lowercase">
              (cap {MAX_VISIBLE_POSITIONS})
            </span>
          </div>
          <MLLegend />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <SortHeader label="Symbol" sortKey="symbol" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Sector" sortKey="sector" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Entry Date" sortKey="entry_date" active={field} dir={dir} onToggle={toggle} />
                <SortHeader label="Entry" sortKey="entry_price" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Current" sortKey="current_price" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Change %" sortKey="unrealized_return_pct" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="SL" sortKey="suggested_sl" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Target" sortKey="suggested_target" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Days" sortKey="days_held" active={field} dir={dir} onToggle={toggle} align="right" />
                <SortHeader label="Score" sortKey="composite_score_at_entry" active={field} dir={dir} onToggle={toggle} align="right" />
                <th
                  className="sticky right-0 z-10 bg-surface-highlight border-l border-border px-2 py-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap"
                  title="SmartFlow ML p_up — second-opinion confidence the stock will return ≥+3% in 5 trading days. HIGH/MID/LOW thresholds: 0.40 / 0.20."
                >
                  ML
                </th>
              </tr>
            </thead>
            <tbody>
              {cappedSorted.map((p) => {
                  const ret = p.unrealized_return_pct || 0;
                  const isWin = ret > 0;
                  const isNew = newInWindowSyms.has(p.symbol);
                  return (
                    <tr key={p.symbol} className="border-b border-border/40 hover:bg-surface-highlight" data-testid={`active-pick-row-${p.symbol}`}>
                      <td className="px-3 py-2.5 font-bold text-foreground">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 cursor-pointer text-left hover:text-accent transition-colors"
                              data-testid={`symbol-cell-${p.symbol}`}
                            >
                              <span className="underline decoration-dotted decoration-muted-foreground/60 underline-offset-4">
                                {p.symbol}
                              </span>
                              <Info className="w-3 h-3 text-muted-foreground/60" />
                              <EventBadge symbol={p.symbol} />
                              {isNew && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-sm"
                                  title={`Newly added within the last ${windowSpec.label.toLowerCase()}`}
                                  data-testid={`new-badge-${p.symbol}`}
                                >
                                  <Sparkles className="w-2.5 h-2.5" />
                                  NEW
                                </span>
                              )}
                              {/* 147j+16bt — Inline verdict chip so power users
                                  reading the table get the same one-glance
                                  recommendation that the rebalance card shows.
                                  Detailed score + ML columns remain to the right
                                  for sortability. */}
                              {(() => {
                                const v = verdictForPick(
                                  p.composite_score_at_entry,
                                  mlBySymbol.get(p.symbol?.toUpperCase()),
                                  { isExit: false },
                                );
                                return (
                                  <ActionBadge
                                    verdict={v}
                                    testId={`row-verdict-${p.symbol}`}
                                  />
                                );
                              })()}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            side="right"
                            align="start"
                            sideOffset={8}
                            className="w-[340px] p-0 bg-surface border-border"
                            data-testid={`factor-popover-${p.symbol}`}
                          >
                            <FactorBreakdownPanel
                              symbol={p.symbol}
                              factorScores={p.factor_scores}
                              factorDetails={p.factor_details}
                              compositeLatest={p.composite_score_latest}
                              compositeAtEntry={p.composite_score_at_entry}
                              scoredDate={p.factor_scored_date}
                              ml={mlBySymbol.get(p.symbol?.toUpperCase())}
                            />
                          </PopoverContent>
                        </Popover>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[140px]">{p.sector}</td>
                      <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground whitespace-nowrap">
                        {(p.entry_date || '').slice(0, 10)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">₹{formatMoney(p.entry_price)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">₹{formatMoney(p.current_price)}</td>
                      <td className={`px-3 py-2.5 text-right font-mono text-xs font-bold ${isWin ? 'text-emerald-400' : ret < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        <div className="flex items-center justify-end gap-1">
                          {isWin && <TrendingUp className="w-3 h-3" />}
                          {ret < 0 && <TrendingDown className="w-3 h-3" />}
                          {formatPct(ret)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] text-red-400/70">
                        {p.suggested_sl ? `₹${formatMoney(p.suggested_sl)}` : '–'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] text-emerald-400/70">
                        {p.suggested_target ? `₹${formatMoney(p.suggested_target)}` : '–'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-xs text-muted-foreground">{p.days_held || 0}d</td>
                      <td className="px-3 py-2.5 text-right text-xs font-mono font-semibold">{p.composite_score_at_entry?.toFixed(1) || '–'}</td>
                      <td className="sticky right-0 z-10 bg-surface group-hover:bg-surface-highlight border-l border-border px-2 py-2.5 text-right whitespace-nowrap">
                        <MLBadge
                          ml={mlBySymbol.get(p.symbol?.toUpperCase())}
                          testId={`ml-badge-${p.symbol}`}
                        />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session 146 — Recently Closed & churn window selector */}
      <div className="bg-surface border border-border overflow-hidden" data-testid="recently-closed-section">
        <div className="px-3 py-2 border-b border-border bg-surface-highlight flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            <History className="w-3.5 h-3.5" />
            Recently Closed
            <span className="text-foreground font-mono normal-case ml-1">
              · {closedWindow.length}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1" data-testid="churn-window-selector">
            {CHURN_WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => setChurnWindow(w.key)}
                className={`px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${
                  churnWindow === w.key
                    ? 'bg-accent/10 text-accent border-accent'
                    : 'bg-surface border-border text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`churn-window-${w.key}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {closedWindow.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">
            No stocks closed in the selected window. Exited picks will appear
            here with realized P&L so you can track churn at a glance.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Symbol</th>
                  <th className="px-3 py-2 text-left">Sector</th>
                  <th className="px-3 py-2 text-left">Exit Date</th>
                  <th className="px-3 py-2 text-right">Entry</th>
                  <th className="px-3 py-2 text-right">Exit</th>
                  <th className="px-3 py-2 text-right">Return</th>
                  <th className="px-3 py-2 text-right">Days</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {closedWindow.map((p, i) => {
                  const r = p.realized_return_pct || 0;
                  const isWin = r > 0;
                  // Session 2026-07-26 — smart-exit retrospective badge
                  const replayKey = `${(p.symbol || '').toUpperCase()}::${(p.entry_date || '').slice(0, 10)}`;
                  const replay = replaysByKey[replayKey];
                  const hasBetterReplay = replay && replay.delta_pct > 0.5;
                  const hasWorseReplay = replay && replay.delta_pct < -0.5;
                  return (
                    <tr
                      key={`${p.symbol}-${p.exit_date || i}`}
                      className="border-b border-border/40 hover:bg-surface-highlight"
                      data-testid={`closed-row-${p.symbol}`}
                    >
                      <td className="px-3 py-2 font-bold text-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{p.symbol}</span>
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-rose-500/15 text-rose-300 border border-rose-500/40 rounded-sm"
                            title="Closed and removed from active portfolio"
                            data-testid={`closed-badge-${p.symbol}`}
                          >
                            CLOSED
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[140px]">{p.sector}</td>
                      <td className="px-3 py-2 text-xs font-mono text-muted-foreground whitespace-nowrap">{(p.exit_date || '').slice(0, 10)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(p.entry_price)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(p.exit_price)}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${isWin ? 'text-emerald-400' : r < 0 ? 'text-rose-400' : 'text-muted-foreground'}`}>
                        <div className="flex items-center justify-end gap-1">
                          {isWin && <TrendingUp className="w-3 h-3" />}
                          {r < 0 && <TrendingDown className="w-3 h-3" />}
                          {formatPct(r)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-muted-foreground">{p.days_held || 0}d</td>
                      <td className="px-3 py-2 text-[10px] text-muted-foreground">
                        <div className="flex flex-col gap-0.5">
                          <span>{(p.exit_reason || 'rebalance').replace(/_/g, ' ')}</span>
                          {replay && (() => {
                            const simPl = replay.sim_return_pct || 0;
                            const delta = replay.delta_pct || 0;
                            const simSign = simPl >= 0 ? '+' : '';
                            const deltaSign = delta >= 0 ? '+' : '';
                            const impactLabel = delta > 0.5
                              ? 'Net Savings in P&L'
                              : delta < -0.5
                              ? 'Net Loss in P&L'
                              : 'Net Impact';
                            return (
                              <span
                                className={`inline-flex flex-col gap-0.5 px-1.5 py-1 text-[9px] font-mono rounded-sm border leading-tight ${
                                  hasBetterReplay
                                    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
                                    : hasWorseReplay
                                    ? 'bg-amber-500/10 text-amber-300 border-amber-500/40'
                                    : 'bg-muted/30 text-muted-foreground border-border'
                                }`}
                                title={
                                  `Smart-exit would fire ${replay.sim_reason} on ${(replay.sim_exit_date || '').slice(0, 10)} `
                                  + `at ₹${(replay.sim_exit_price || 0).toFixed(2)}. `
                                  + `Detail: ${replay.sim_detail || '—'}`
                                }
                                data-testid={`smart-exit-badge-${p.symbol}`}
                              >
                                <span>
                                  <span className="opacity-75">Smart exit:</span>{' '}
                                  <span className="font-semibold">{replay.sim_reason || '—'}</span>{' '}
                                  <span className="font-bold">P/L {simSign}{simPl.toFixed(2)}%</span>
                                </span>
                                <span className={`font-semibold ${
                                  delta > 0.5 ? 'text-emerald-200'
                                  : delta < -0.5 ? 'text-amber-200'
                                  : 'text-muted-foreground'
                                }`}>
                                  {impactLabel}: {deltaSign}{delta.toFixed(2)}%
                                </span>
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {closed.count > 0 && (
          <div className="px-3 py-2 border-t border-border bg-surface/60 text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span>Lifetime: {closed.count} closed</span>
            <span>Win rate {closed.win_rate_pct}%</span>
            <span>Avg return {formatPct(closed.avg_realized_return_pct)}</span>
            <span>Avg hold {closed.avg_hold_days}d</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * TodaysRebalanceBanner — prominent callout shown when today's rebalance
 * produced any exit or entry. Each chip is hover-tooltipped with the full
 * trade detail so the user knows exactly which stock to sell vs buy.
 *
 * Session 145h: before this, the user had to cross-reference Current tab
 * vs Past tab vs today's date to reconstruct the delta. Now it's one
 * glance at the top.
 *
 * Session 147j+16bt: the dual `73.9 + 12%` chip was confusing — two
 * unrelated numbers in different colors sitting next to each other.
 * Replaced with a single combined ACTION verdict badge (STRONG BUY /
 * BUY / BUY · HALF SIZE / SELL) that the user can act on without any
 * interpretation. Raw numbers live in the hover tooltip for power users.
 */

// ===================================================================
// Session 147j+16bt — Combined Action verdict.
// Maps (score, ML) → human verdict + tone + sizing recommendation.
// Single source of truth used by Entry chip, Exit chip, and the
// Active Positions table.
// ===================================================================
//   STRONG BUY  → quant score ≥ floor AND flow ML ≥ 40%
//   BUY         → quant score ≥ floor AND flow ML ≥ 20% (or no ML)
//   HALF SIZE   → quant score ≥ floor AND flow ML < 20%
//   SELL        → exit row
// ===================================================================
const verdictForPick = (score, ml, { isExit = false } = {}) => {
  if (isExit) {
    return {
      key: 'SELL',
      label: 'SELL',
      tone: 'border-rose-500/50 bg-rose-500/15 text-rose-200',
      dotTone: 'bg-rose-400',
      why: 'Position closing.',
      sizing: 'Close full position.',
    };
  }
  const hasML = ml && ml.p_up != null;
  const pUp = hasML ? ml.p_up : null;

  if (hasML && pUp >= 0.40) {
    return {
      key: 'STRONG_BUY',
      label: 'STRONG BUY',
      tone: 'border-emerald-400/60 bg-emerald-500/20 text-emerald-200 font-bold',
      dotTone: 'bg-emerald-400',
      why: 'Quant score strong and smart-money trend confirming.',
      sizing: 'Full position.',
    };
  }
  if (hasML && pUp < 0.20) {
    return {
      key: 'HALF_SIZE',
      label: 'BUY · HALF SIZE',
      tone: 'border-amber-400/60 bg-amber-500/15 text-amber-200',
      dotTone: 'bg-amber-400',
      why: 'Quant score strong but smart-money trend is weak.',
      sizing: 'Half position. Wait for the trend to turn before sizing up.',
    };
  }
  // hasML && 0.20–0.40, OR no ML at all.
  return {
    key: 'BUY',
    label: 'BUY',
    tone: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    dotTone: hasML ? 'bg-emerald-400' : 'bg-zinc-500',
    why: hasML
      ? 'Quant agrees; smart-money trend neutral.'
      : 'Quant agrees; no smart-money trend data available.',
    sizing: 'Full position.',
    noFlowData: !hasML,
  };
};

// ===================================================================
// ActionBadge — the visual element. Used everywhere a verdict needs
// to render. Tiny, scannable, one word does the work of two numbers.
// ===================================================================
const ActionBadge = ({ verdict, realizedPct = null, testId }) => {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-sm whitespace-nowrap ${verdict.tone}`}
      title={`${verdict.label} — ${verdict.why} ${verdict.sizing}`}
      data-testid={testId}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${verdict.dotTone}`} />
      {verdict.label}
      {verdict.noFlowData && (
        <span className="text-zinc-500 text-[9px] normal-case tracking-normal ml-0.5">
          no trend data
        </span>
      )}
      {realizedPct != null && (
        <span className={`ml-1 ${realizedPct > 0 ? 'text-emerald-300' : realizedPct < 0 ? 'text-rose-300' : 'text-muted-foreground'}`}>
          {realizedPct > 0 ? '+' : ''}{realizedPct.toFixed(2)}%
        </span>
      )}
    </span>
  );
};

// ===================================================================
// RebalanceLegend — collapsible info strip explaining the verdicts.
// Open by default for first-time users; preference is persisted in
// localStorage so power users only see it on the first visit.
// ===================================================================
const REBALANCE_LEGEND_KEY = 'alphascout.rebalance.legend.collapsed.v1';

const RebalanceLegend = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(REBALANCE_LEGEND_KEY) === '1';
    } catch (_) {
      return false;
    }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(REBALANCE_LEGEND_KEY, next ? '1' : '0');
    } catch (_) { /* noop — private browsing */ }
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid="rebalance-legend-show"
      >
        <Info className="w-3 h-3" />
        What do these verdicts mean?
      </button>
    );
  }

  const items = [
    { k: 'STRONG_BUY', dot: 'bg-emerald-400', label: 'STRONG BUY', desc: 'Both quant + trend agree — full position.' },
    { k: 'BUY',        dot: 'bg-emerald-400', label: 'BUY',        desc: 'Quant agrees, trend neutral — full position.' },
    { k: 'HALF_SIZE',  dot: 'bg-amber-400',   label: 'BUY · HALF SIZE', desc: 'Quant yes, trend weak — half position.' },
    { k: 'SELL',       dot: 'bg-rose-400',    label: 'SELL',       desc: 'Closing the position.' },
  ];

  return (
    <div
      className="border border-border bg-surface/60 px-3 py-2 text-[11px] space-y-1.5"
      data-testid="rebalance-legend"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-foreground/80">
          <Info className="w-3 h-3" />
          Verdict = AlphaScout quant score + smart-money trend
        </div>
        <button
          type="button"
          onClick={toggle}
          className="text-muted-foreground hover:text-foreground text-[10px] underline-offset-2 hover:underline"
          data-testid="rebalance-legend-hide"
        >
          hide
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-1 pt-1">
        {items.map((it) => (
          <div key={it.k} className="flex items-center gap-1.5 text-muted-foreground">
            <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${it.dot}`} />
            <span className="font-mono font-semibold text-foreground/85">{it.label}</span>
            <span className="text-[10px]">{it.desc}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground/80 pt-1 border-t border-border/40 mt-1">
        <span className="text-foreground/70 font-semibold">Smart-money trend</span> reads
        bulk deals, FII / DII activity, open interest, and VWAP to estimate the chance of a
        +3% move in the next 5 trading days.
      </div>
    </div>
  );
};

const TodaysRebalanceBanner = ({ exits, entries, todayLabel, mlBySymbol }) => {
  const exitCount = exits.length;
  const entryCount = entries.length;
  const netChange = entryCount - exitCount;

  return (
    <div
      className="bg-accent/5 border border-accent/40 p-4 space-y-3"
      data-testid="todays-rebalance-banner"
    >
      <div className="flex items-center gap-2">
        <ArrowRightLeft className="w-4 h-4 text-accent" />
        <h3 className="font-heading font-semibold text-sm">
          Today&apos;s Rebalance
        </h3>
        <span className="text-[11px] text-muted-foreground font-mono">
          {todayLabel}
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {exitCount} exit{exitCount === 1 ? '' : 's'} · {entryCount} entr
          {entryCount === 1 ? 'y' : 'ies'}
          {netChange !== 0 && (
            <span
              className={`ml-1.5 font-mono ${
                netChange > 0 ? 'text-emerald-400' : 'text-rose-400'
              }`}
            >
              ({netChange > 0 ? '+' : ''}
              {netChange})
            </span>
          )}
        </span>
      </div>

      {/* 147j+16bt — Verdict legend (collapsible, localStorage-remembered) */}
      <RebalanceLegend />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Exits */}
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-rose-400 font-semibold">
            <ArrowDownCircle className="w-3.5 h-3.5" />
            Sell
            <span className="text-muted-foreground font-normal normal-case">
              — close these positions
            </span>
          </div>
          {exitCount === 0 ? (
            <div className="text-xs text-muted-foreground italic">No exits today.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {exits.map((p) => (
                <ExitChip key={`exit-${p.symbol}-${p.exit_date}`} p={p} ml={mlBySymbol?.get(p.symbol?.toUpperCase())} />
              ))}
            </div>
          )}
        </div>

        {/* Entries */}
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">
            <ArrowUpCircle className="w-3.5 h-3.5" />
            Buy
            <span className="text-muted-foreground font-normal normal-case">
              — open these positions
            </span>
          </div>
          {entryCount === 0 ? (
            <div className="text-xs text-muted-foreground italic">No entries today.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {entries.map((p) => (
                <EntryChip key={`entry-${p.symbol}-${p.entry_date}`} p={p} ml={mlBySymbol?.get(p.symbol?.toUpperCase())} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Session 147i — MLBadge shared between active-positions table & chips.
//   • HIGH (p_up ≥ 0.40)  → emerald   "ML strongly agrees"
//   • MID  (0.20 ≤ p_up)  → amber
//   • LOW  (p_up < 0.20)  → rose      "ML disagrees — consider half-size"
//   • NONE (no row)       → grey "—"  symbol outside SmartFlow universe
const ML_TONES = {
  HIGH: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
  MID: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
  LOW: 'border-rose-500/40 bg-rose-500/15 text-rose-300',
  NONE: 'border-zinc-700/40 bg-zinc-800/30 text-zinc-500',
};

const MLBadge = ({ ml, compact = false, testId }) => {
  if (!ml) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase border rounded-sm ${ML_TONES.NONE}`}
        title="No SmartFlow ML opinion (symbol outside microstructure universe)"
        data-testid={testId}
      >
        — ML
      </span>
    );
  }
  const tone = ML_TONES[ml.verdict] || ML_TONES.NONE;
  const pct = ml.p_up != null ? `${(ml.p_up * 100).toFixed(0)}%` : '—';
  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase border rounded-sm ${tone}`}
        title={`SmartFlow ML: P(+3% / 5d) = ${pct} · Verdict: ${ml.verdict}`}
        data-testid={testId}
      >
        {pct}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase border rounded-sm ${tone}`}
      title={`SmartFlow ML: P(+3% / 5d) = ${pct} · Verdict: ${ml.verdict}`}
      data-testid={testId}
    >
      {ml.label} · {pct}
    </span>
  );
};


// Exit chip — face shows SELL verdict + realized %. Hover shows full detail.
const ExitChip = ({ p, ml }) => {
  const r = p.realized_return_pct;
  const retTone = r > 0 ? 'text-emerald-400' : r < 0 ? 'text-rose-400' : 'text-muted-foreground';
  const verdict = verdictForPick(null, ml, { isExit: true });
  return (
    <div className="relative group">
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 border border-rose-500/40 bg-rose-500/10 text-xs font-mono cursor-help"
        data-testid={`exit-chip-${p.symbol}`}
      >
        <span className="font-semibold text-foreground">{p.symbol}</span>
        <ActionBadge verdict={verdict} realizedPct={r} testId={`exit-verdict-${p.symbol}`} />
      </div>
      <RebalanceTooltip title={`SELL · ${p.symbol}`}>
        <TooltipRow label="Verdict" value={verdict.label} tone="text-rose-300" />
        <TooltipRow label="Sector" value={p.sector || '—'} />
        <TooltipRow
          label="Entry"
          value={`${formatMoney(p.entry_price)} on ${(p.entry_date || '').slice(0, 10)}`}
        />
        <TooltipRow
          label="Exit"
          value={`${formatMoney(p.exit_price)} on ${(p.exit_date || '').slice(0, 10)}`}
        />
        <TooltipRow
          label="Realized P&L"
          value={`${r > 0 ? '+' : ''}${(r ?? 0).toFixed(2)}%`}
          tone={retTone}
        />
        <TooltipRow label="Hold" value={`${p.hold_days ?? '—'} days`} />
        <TooltipRow label="Reason" value={p.exit_reason || 'rebalance'} />
        {p.composite_score_at_entry != null && (
          <TooltipRow
            label="Score @ entry"
            value={Number(p.composite_score_at_entry).toFixed(1)}
          />
        )}
      </RebalanceTooltip>
    </div>
  );
};

// Entry chip — face shows a single combined action verdict (STRONG BUY /
// BUY / BUY · HALF SIZE). Hover reveals all the underlying numbers
// (composite score, ML p_up, sector, entry price) for power users.
const EntryChip = ({ p, ml }) => {
  const score = p.composite_score_at_entry;
  const verdict = verdictForPick(score, ml, { isExit: false });
  const pUpPct = ml && ml.p_up != null ? `${(ml.p_up * 100).toFixed(0)}%` : '—';
  return (
    <div className="relative group">
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-xs font-mono cursor-help"
        data-testid={`entry-chip-${p.symbol}`}
      >
        <span className="font-semibold text-foreground">{p.symbol}</span>
        <ActionBadge verdict={verdict} testId={`entry-verdict-${p.symbol}`} />
      </div>
      <RebalanceTooltip title={`BUY · ${p.symbol}`}>
        <TooltipRow label="Verdict" value={verdict.label} tone="text-emerald-300" />
        <TooltipRow label="Why" value={verdict.why} />
        <TooltipRow label="Sizing" value={verdict.sizing} />
        <TooltipRow label="Sector" value={p.sector || '—'} />
        <TooltipRow label="Entry price" value={formatMoney(p.entry_price)} />
        <TooltipRow
          label="Quant score"
          value={`${Number(score ?? 0).toFixed(1)} / 100`}
          tone="text-emerald-400"
        />
        <TooltipRow
          label="Smart-money trend (5d)"
          value={
            ml
              ? `${ml.verdict === 'HIGH' ? 'STRONG' : ml.verdict === 'LOW' ? 'WEAK' : 'NEUTRAL'} — ${pUpPct} chance of +3% move`
              : 'no data — symbol outside SmartFlow universe'
          }
          tone={
            ml && ml.verdict === 'HIGH' ? 'text-emerald-400'
              : ml && ml.verdict === 'LOW' ? 'text-rose-400'
              : ml && ml.verdict === 'MID' ? 'text-amber-400'
              : 'text-muted-foreground'
          }
        />
        <TooltipRow label="Entry date" value={(p.entry_date || '').slice(0, 10)} />
        {p.unrealized_return_pct != null && (
          <TooltipRow
            label="Unrealized P&L"
            value={`${p.unrealized_return_pct > 0 ? '+' : ''}${p.unrealized_return_pct.toFixed(2)}%`}
            tone={p.unrealized_return_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}
          />
        )}
      </RebalanceTooltip>
    </div>
  );
};

// Shared tooltip shell — CSS-only, appears on hover of parent `.group`.
const RebalanceTooltip = ({ title, children }) => (
  <div className="pointer-events-none absolute left-0 top-full mt-1 z-20 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150">
    <div className="bg-surface border border-border shadow-lg p-3 min-w-[260px]">
      <div className="text-[11px] font-mono font-semibold text-foreground mb-2 pb-1 border-b border-border">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  </div>
);

const TooltipRow = ({ label, value, tone }) => (
  <div className="flex items-center justify-between gap-4 text-[11px] font-mono">
    <span className="text-muted-foreground">{label}</span>
    <span className={tone || 'text-foreground'}>{value}</span>
  </div>
);

// Session 147j+1 — FactorBreakdownPanel surfaces the 5-factor scoring
// rationale inside a Radix Popover (portaled, escapes table overflow).
// Mirrors the AlphaScout factor radar but inline so the user can answer
// "why is this stock in my portfolio?" without leaving the table.
const FACTOR_DEFS = [
  { key: 'momentum', label: 'Momentum', tone: 'text-amber-300', desc: '% returns / volatility (1m + 3m)' },
  { key: 'technical', label: 'Technical', tone: 'text-sky-300', desc: 'Trend, RSI, SMA stack, breakouts' },
  { key: 'value', label: 'Value', tone: 'text-emerald-300', desc: 'PE/PB vs sector median' },
  { key: 'quality', label: 'Quality', tone: 'text-violet-300', desc: 'ROE, debt/equity, margins' },
  { key: 'smart_money', label: 'Smart Money', tone: 'text-rose-300', desc: 'Volume, delivery%, large-cap flow' },
  // Session 147j+7 — LODR Phase B order-flow factor. Shown only when
  // corp_orders data is present for the symbol (else `null` in payload).
  { key: 'order_flow', label: 'Order Flow', tone: 'text-cyan-300', desc: 'Book-to-bill, order / mcap, 90d momentum (LODR)' },
];

const FactorBar = ({ score }) => {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const tone =
    v >= 70 ? 'bg-emerald-500'
      : v >= 50 ? 'bg-amber-400'
        : v >= 30 ? 'bg-orange-400'
          : 'bg-rose-500';
  return (
    <div className="flex-1 h-1.5 bg-surface-highlight border border-border/60 overflow-hidden ml-2">
      <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
    </div>
  );
};

// Session 147j+16k — Pretty-formats a factor_details key/value pair so
// the popover surfaces actual data (PE 14.2, ROE 18%, RSI 62, etc.)
// rather than just the generic factor description. This is what the
// user has been calling the "missing explanation" on Hunter picks.
const _formatDetailKey = (k) =>
  String(k || '')
    .replace(/_/g, ' ')
    .replace(/\bpct\b/gi, '%')
    .replace(/\bpe\b/gi, 'PE')
    .replace(/\bpb\b/gi, 'PB')
    .replace(/\broe\b/gi, 'ROE')
    .replace(/\brsi\b/gi, 'RSI')
    .replace(/\bsma\b/gi, 'SMA')
    .replace(/\bvwap\b/gi, 'VWAP')
    .replace(/\boi\b/gi, 'OI')
    .replace(/\bmcap\b/gi, 'MCap')
    // Session 2026-07-26 — friendlier delivery-signal labels
    .replace(/^delivery bonus pts$/i, 'Delivery bonus')
    .replace(/^delivery fires 10d$/i, 'Delivery fires (10d)')
    .replace(/^delivery tier max$/i, 'Delivery tier')
    .replace(/^delivery z max$/i, 'Delivery z-score')
    .trim();

const _formatDetailValue = (v) => {
  if (v == null) return '—';
  if (typeof v === 'number') {
    // Session 2026-07-26 — integers render as-is (delivery_bonus_pts=8,
    // delivery_fires_10d=1). Small fractions get 3dp, big numbers stay whole.
    if (Number.isInteger(v)) return String(v);
    if (Math.abs(v) < 1 && v !== 0) return v.toFixed(3);
    if (Math.abs(v) >= 1000) return v.toFixed(0);
    return v.toFixed(2);
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const FactorDetailList = ({ details }) => {
  if (!details || typeof details !== 'object') return null;
  const entries = Object.entries(details).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  if (!entries.length) return null;
  // Session 2026-07-26 — bumped from 6 → 8 items so the new
  // delivery_bonus_pts / delivery_fires_10d / delivery_tier_max /
  // delivery_z_max fields on SmartMoney all render.
  return (
    <div className="pl-1 mt-0.5 grid grid-cols-2 gap-x-2 gap-y-0.5">
      {entries.slice(0, 8).map(([k, v]) => {
        const isDeliveryKey = k.startsWith('delivery_');
        const isBonusHit = k === 'delivery_bonus_pts' && Number(v) > 0;
        return (
          <div
            key={k}
            className={`flex items-center justify-between text-[9px] font-mono ${
              isDeliveryKey ? 'text-emerald-300/90' : 'text-muted-foreground/90'
            }`}
            data-testid={`factor-detail-${k}`}
          >
            <span className="truncate mr-1">{_formatDetailKey(k)}</span>
            <span
              className={`font-semibold ${
                isBonusHit ? 'text-emerald-200' : 'text-foreground/90'
              }`}
            >
              {isBonusHit ? `+${_formatDetailValue(v)}` : _formatDetailValue(v)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const FactorBreakdownPanel = ({
  symbol, factorScores, factorDetails, compositeLatest, compositeAtEntry, scoredDate, ml,
}) => {
  const hasScores = factorScores && Object.keys(factorScores).length > 0;
  const details = factorDetails || {};
  return (
    <div
      className="p-3"
      data-testid={`factor-tooltip-${symbol}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2 pb-1 border-b border-border">
        <div>
          <div className="text-[11px] font-mono font-semibold text-foreground">
            Why {symbol}?
          </div>
          <div className="text-[10px] text-muted-foreground">
            Factor breakdown · scored on{' '}
            <span className="font-mono">{scoredDate ? String(scoredDate).slice(0, 10) : '—'}</span>
          </div>
        </div>
        {compositeLatest != null && (
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
              Composite
            </div>
            <div className="text-base font-mono font-bold text-foreground">
              {Number(compositeLatest).toFixed(1)}
            </div>
          </div>
        )}
      </div>
      {hasScores ? (
        <div className="space-y-2">
          {FACTOR_DEFS.map((f) => {
            const s = factorScores[f.key];
            if (s == null) return null;
            const d = details[f.key];
            return (
              <div
                key={f.key}
                className="space-y-0.5"
                data-testid={`factor-row-${symbol}-${f.key}`}
              >
                <div className="flex items-center justify-between gap-2 text-[10px] font-mono">
                  <span className={`${f.tone} uppercase tracking-wider font-semibold`}>
                    {f.label}
                  </span>
                  <FactorBar score={s} />
                  <span className="text-foreground w-8 text-right font-bold">
                    {Number(s).toFixed(0)}
                  </span>
                </div>
                <div className="text-[9px] text-muted-foreground/80 italic pl-1">
                  {f.desc}
                </div>
                <FactorDetailList details={d} />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground italic">
          No factor breakdown available — score data not yet persisted.
        </div>
      )}
      <div className="mt-3 pt-2 border-t border-border space-y-1">
        {compositeAtEntry != null && (
          <TooltipRow
            label="Score @ entry"
            value={Number(compositeAtEntry).toFixed(1)}
          />
        )}
        {ml && (
          <TooltipRow
            label="SmartFlow ML"
            value={`${ml.label} · ${ml.p_up != null ? (ml.p_up * 100).toFixed(0) + '%' : '—'}`}
            tone={
              ml.verdict === 'HIGH' ? 'text-emerald-400'
                : ml.verdict === 'LOW' ? 'text-rose-400'
                  : ml.verdict === 'MID' ? 'text-amber-400'
                    : 'text-muted-foreground'
            }
          />
        )}
      </div>
    </div>
  );
};

// Session 147j+1 — MLLegend pill row shown above the active-positions
// table. Surfaces the HIGH / MID / LOW thresholds inline so the user
// doesn't have to hover the column header to remember what they mean.
const MLLegend = () => (
  <div
    className="flex items-center gap-2 ml-auto text-[10px] font-mono uppercase tracking-wider"
    data-testid="ml-legend"
  >
    <span className="text-muted-foreground/80 normal-case lowercase">SmartFlow ML:</span>
    <span className="inline-flex items-center px-1.5 py-0.5 border border-emerald-500/40 bg-emerald-500/15 text-emerald-300">
      HIGH ≥40%
    </span>
    <span className="inline-flex items-center px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/15 text-amber-300">
      MID 20–40%
    </span>
    <span className="inline-flex items-center px-1.5 py-0.5 border border-rose-500/40 bg-rose-500/15 text-rose-300">
      LOW &lt;20%
    </span>
  </div>
);


/**
 * CronHeartbeatMarker — Session 146o nightly-job verification pill.
 * Shows the last successful run of the 16:30 IST AlphaScout cron.
 *
 * Visual state:
 *  - GREEN check  → ran within last 20 h (cron is healthy)
 *  - AMBER clock  → 20–30 h (possibly stale, could be weekend)
 *  - RED alert    → >30 h OR the endpoint replied `ok: false` (cron skipped)
 *
 * Hover reveals: last_run_ist_readable, duration, picks_count, fetch_errors.
 */

/**
 * Session 2026-07-26 — BhavcopyStatusMarker
 * Shows a compact pill with the freshness of the daily bhavcopy pipeline.
 * Hover reveals the full per-stage breakdown of the latest run.
 */
const BhavcopyStatusMarker = ({ bs }) => {
  if (!bs) return null;
  const verdict = bs.verdict || 'never';
  const tones = {
    fresh:            { cls: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300', Icon: CheckCircle2, label: 'Bhavcopy fresh' },
    stale_1d:         { cls: 'border-amber-500/50 bg-amber-500/10 text-amber-300',       Icon: Clock,          label: 'Bhavcopy 1d old' },
    stale:            { cls: 'border-amber-500/50 bg-amber-500/10 text-amber-300',       Icon: AlertTriangle,  label: 'Bhavcopy stale' },
    very_stale:       { cls: 'border-rose-500/50 bg-rose-500/10 text-rose-300',          Icon: AlertTriangle,  label: 'Bhavcopy VERY stale' },
    never:            { cls: 'border-rose-500/50 bg-rose-500/10 text-rose-300',          Icon: AlertTriangle,  label: 'Bhavcopy never' },
    weekend_data_bug: { cls: 'border-rose-500/50 bg-rose-500/10 text-rose-300',          Icon: AlertTriangle,  label: 'Bhavcopy data bug' },
  };
  const t = tones[verdict] || tones.never;
  const Icon = t.Icon;

  const dateStr = bs.latest_bhavcopy_date || '—';
  const rowCount = bs.latest_bhavcopy_rows || 0;

  return (
    <div className="relative group" data-testid="bhavcopy-status-marker">
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border cursor-help ${t.cls}`}
        data-testid={`bhavcopy-status-${verdict}`}
      >
        <Icon className="w-3 h-3" />
        {t.label} · {dateStr}
      </span>
      <BhavcopyStatusTooltip bs={bs} rowCount={rowCount} />
    </div>
  );
};

const BhavcopyStatusTooltip = ({ bs, rowCount }) => {
  const last = bs?.last_run;
  const stages = last?.stages || {};
  const stageEntries = Object.entries(stages);
  return (
    <div className="pointer-events-none absolute right-0 top-full mt-1 z-20 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150">
      <div className="bg-surface border border-border shadow-lg p-3 min-w-[320px]">
        <div className="text-[11px] font-mono font-semibold text-foreground mb-2 pb-1 border-b border-border">
          Daily Bhavcopy Pipeline
        </div>
        <div className="space-y-1 text-[11px]">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Latest bhavcopy</span>
            <span className="font-mono text-foreground">
              {bs?.latest_bhavcopy_date || '—'} · {rowCount} rows
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Expected trading day</span>
            <span className="font-mono text-foreground">
              {bs?.expected_trading_day || '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Trading days behind</span>
            <span className={`font-mono ${(bs?.days_behind || 0) > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
              {bs?.days_behind == null ? '—' : `${bs.days_behind}d`}
            </span>
          </div>
          {last && (
            <>
              <div className="mt-2 pt-2 border-t border-border">
                <div className="flex justify-between mb-1">
                  <span className="text-muted-foreground">Last run ({last.trigger || 'scheduled'})</span>
                  <span className="font-mono text-foreground">
                    {last.summary?.stages_ok || 0} ok / {last.summary?.stages_error || 0} err
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Ran on</span>
                  <span className="font-mono text-foreground">{last.run_date || '—'}</span>
                </div>
                <div className="space-y-0.5">
                  {stageEntries.map(([name, s]) => (
                    <div key={name} className="flex justify-between items-center gap-2 text-[10px]">
                      <span className="truncate text-muted-foreground">{name.replace(/_/g, ' ')}</span>
                      <span
                        className={`font-mono ${
                          s.status === 'ok' ? 'text-emerald-300'
                          : s.status === 'error' ? 'text-rose-300'
                          : 'text-muted-foreground'
                        }`}
                        title={s.error || ''}
                      >
                        {s.status === 'ok' ? `✓ ${s.elapsed_s || 0}s` : s.status === 'error' ? '✗ err' : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          {!last && (
            <div className="text-muted-foreground italic mt-2">
              No pipeline run recorded yet. Auto-runs 18:45 IST Mon–Fri, or trigger manually via admin API.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const CronHeartbeatMarker = ({ hb }) => {
  if (!hb) {
    return null;
  }

  // Endpoint returns { ok: false, message } if no successful run yet.
  if (hb.ok === false) {
    return (
      <div className="relative group" data-testid="cron-heartbeat-marker">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-rose-500/50 bg-rose-500/10 text-rose-300 cursor-help"
          data-testid="cron-heartbeat-never"
        >
          <AlertTriangle className="w-3 h-3" />
          Cron · never ran
        </span>
        <CronHeartbeatTooltip hb={hb} />
      </div>
    );
  }

  const ageMin = hb.age_minutes;
  const ageHours = typeof ageMin === 'number' ? ageMin / 60 : null;
  let tone = 'emerald';
  let Icon = CheckCircle2;
  let label = 'Cron ok';
  if (ageHours == null) {
    tone = 'muted';
    Icon = Clock;
    label = 'Cron';
  } else if (ageHours > 30) {
    tone = 'rose';
    Icon = AlertTriangle;
    label = 'Cron stale';
  } else if (ageHours > 20) {
    tone = 'amber';
    Icon = Clock;
    label = 'Cron stale?';
  }

  const toneClasses = {
    emerald: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300',
    amber: 'border-amber-500/50 bg-amber-500/10 text-amber-300',
    rose: 'border-rose-500/50 bg-rose-500/10 text-rose-300',
    muted: 'border-border bg-surface text-muted-foreground',
  }[tone];

  // Parse HH:MM IST from readable string "2026-04-28 16:30:47 IST"
  const timeHM = (hb.last_run_ist_readable || '').match(/(\d{2}:\d{2})/)?.[1] || '—';

  return (
    <div className="relative group" data-testid="cron-heartbeat-marker">
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border cursor-help ${toneClasses}`}
        data-testid={`cron-heartbeat-${tone}`}
      >
        <Icon className="w-3 h-3" />
        {label} · {timeHM} IST
      </span>
      <CronHeartbeatTooltip hb={hb} />
    </div>
  );
};

const CronHeartbeatTooltip = ({ hb }) => (
  <div className="pointer-events-none absolute right-0 top-full mt-1 z-20 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150">
    <div className="bg-surface border border-border shadow-lg p-3 min-w-[260px]">
      <div className="text-[11px] font-mono font-semibold text-foreground mb-2 pb-1 border-b border-border">
        AlphaScout Nightly Cron
      </div>
      <div className="space-y-1">
        {hb.ok === false ? (
          <div className="text-[11px] text-muted-foreground">
            {hb.message || 'No successful run recorded yet.'}
          </div>
        ) : (
          <>
            <TooltipRow label="Last run" value={hb.last_run_ist_readable || '—'} />
            <TooltipRow
              label="Age"
              value={
                typeof hb.age_minutes === 'number'
                  ? hb.age_minutes < 60
                    ? `${hb.age_minutes.toFixed(1)} min ago`
                    : `${(hb.age_minutes / 60).toFixed(1)} h ago`
                  : '—'
              }
            />
            <TooltipRow label="Status" value={hb.status || '—'} />
            <TooltipRow
              label="Duration"
              value={hb.duration_seconds != null ? `${hb.duration_seconds}s` : '—'}
            />
            <TooltipRow
              label="Picks"
              value={hb.picks_count != null ? String(hb.picks_count) : '—'}
            />
            <TooltipRow
              label="Fetch errors"
              value={hb.fetch_errors != null ? String(hb.fetch_errors) : '—'}
              tone={hb.fetch_errors ? 'text-amber-400' : undefined}
            />
            {hb.news_red != null && (
              <TooltipRow label="Red news" value={String(hb.news_red)} />
            )}
          </>
        )}
      </div>
    </div>
  </div>
);


const SummaryCard = ({ label, value, subvalue, positive, testid }) => (
  <div className="bg-surface border border-border p-3" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</div>
    <div
      className={`text-lg font-bold font-mono ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    {subvalue && <div className="text-[10px] text-muted-foreground mt-0.5">{subvalue}</div>}
  </div>
);

export default CurrentPicks;
